// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NewsCard from "@stoneage/App/Components/NewsPageTile";
import IconArrowRightCopper from "@stoneage/XDL/Icons/IconArrowRightCopper";
import { color, styledSlider, xScreen } from "@stoneage/XDL/Utilities";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line
import { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

export const newsListCarousel = css`
    ${styledSlider()}

    .swiper-container {
        width: 100% !important;
        padding-bottom: 33px;
    }

    && .swiper-slide {
        padding: 0;
    }

    && .swiper-slide > div:first-child {
        max-width: 583px;
    }

    && .swiper-button-disabled {
        display: block;
    }

    && .swiper-button-next,
    .swiper-button-prev {
        background-color: inherit;
    }

    && .swiper-button-prev {
        position: static;
        height: 24px;
        width: 24px;
        margin-right: 9px;
    }

    && .swiper-button-next {
        position: static;
        height: 24px;
        width: 24px;
    }

    && .swiper-navigation {
        position: absolute;
        bottom: -35px;
        left: 50%;
        transform: translate(-50%);
        display: flex;
        align-items: flex-end;
        justify-content: center;
    }

    .swiper-pagination {
        display: flex;
        position: static;
        height: 24px;
        align-items: center;
        .swiper-pagination-bullet {
            margin-right: 8px !important;
            height: 10px;
            width: 10px;
            background-color: transparent;
            border: 1px solid;
            transform: unset;
        }

        &.light .swiper-pagination-bullet {
            border-color: ${color('common.ui6')};
        }

        &.dark .swiper-pagination-bullet {
            border-color: ${color('common.white')};
        }
    }
    .swiper-pagination.light .swiper-pagination-bullet-active {
        background: ${color('common.ui7')};
    }
    .swiper-pagination.dark .swiper-pagination-bullet-active {
        background: ${color('common.white')};
    }
`;

interface OwnProps extends WidgetProps {
    fields: {
        title: string;
        newsType: string;
        colorTheme: string;
        parentNewsPage: LinkFieldValue;
        carouselSpecificId: string;
    }
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory & OwnProps;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: ownProps.fields.parentNewsPage.value });
    const parentPageLink = getPageLinkByNodeId(state, ownProps.fields.parentNewsPage.value);
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }

    return {
        pages,
        links,
        parentPageLink
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    totalProducts?: TypographyProps;
    topPaginationWrapper?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    topPaginationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 12px;
            margin-bottom: 32px;
            border-top: 1px solid ${color("common.ui6")};
            @media (max-width: 767px) {
                flex-wrap: wrap;
                justify-content: space-between;
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-bottom: 24px;
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
                align-items: baseline;
                gap: 12px;
            }
        `,
    },
    title: {
        variant: "h2",
        lineHeight: "26px",
        css: css`
            margin-bottom: 0;
            margin-right: 32px;
            ${xScreen("lte", "xs")} {
                margin-right: 0;
            }
        `,
    },
    totalProducts: {
        size: "18px",
        lineHeight: "21px",
        weight: "bold",
        color: "common.black",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                display: block;
            }
        `,
    },
    newsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-right: -22px;
            @media (max-width: 650px) {
                margin-right: 0;
            }
        `,
    },
    readMoreLink: {
        css: css`
            display: inline;
            ${IconWrapper} {
                margin-left: 6px;
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
    },
};

const styles = newsListViewStyles;


const today = new Date().getTime();

const comparePublishDate = (page: PageProps) => {
    const publishDate = new Date(page.fields["publishDate"]).getTime();

    return publishDate <= today;
};

const compareExpirationDate = (page: PageProps) => {
    const expirationDate = new Date(page.fields["expirationDate"]).getTime();

    return expirationDate > today;
};

const NewsListView = ({
    pages,
    links,
    loadPagesForParent,
    id,
    parentPageLink,
    fields: { title, newsType, colorTheme, carouselSpecificId, parentNewsPage }
}: Props) => {
    const [showSpinner, setShowSpinner] = useState(false);
    const [slides, setSlides] = useState(1);

    useEffect(() => {
        window.addEventListener("resize", getDimensions);
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };
        getStyles().then(result => {
            setShowSpinner(false);
        });
        return () => {
            window.removeEventListener("resize", getDimensions);
        };
    }, []);

    const getDimensions = (swiper: any) => {
        if (swiper?.params) {
            const elementWidth = document.getElementsByClassName(`${id}`)[0]?.getBoundingClientRect().width;
            if (!elementWidth) {
                return;
            }

            if (elementWidth < 550 || window?.innerWidth < 580) {
                setSlides(1);
            } else if ((elementWidth <= 760 && elementWidth > 580) || window?.innerWidth <= 768) {
                setSlides(2);
            } else {
                setSlides(3);
            }
        }
    };

    const assignSwiper = (swiper: any) => {
        getDimensions(swiper);
    };

    useEffect(() => {
        if (!pages?.value && !pages?.isLoading) {
            loadPagesForParent({
                page: 1,
                pageSize: 999,
                parentNodeId: parentNewsPage.value
            });
        }
    }, []);

    if (showSpinner) {
        return (
            <StyledWrapper
                id={`${carouselSpecificId}`}
                css={css`
                    height: 100px;
                    width: 100%;
                    padding: 10px 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }


    const filteredPages = pages?.value?.filter(page => page.generalFields.newsPageType === newsType);

    if (!filteredPages || !filteredPages.length) {
        return null;
    }
    let sortedAndPagedPages = sortBy(filteredPages, o => o.fields["publishDate"]);
    sortedAndPagedPages.reverse();

    sortedAndPagedPages = sortedAndPagedPages.filter(page => comparePublishDate(page));
    sortedAndPagedPages = sortedAndPagedPages.filter(page => compareExpirationDate(page));

    const getTitleColor = () => (colorTheme === "light" ? "common.black" : "common.white");

    const getIconColor = () => colorTheme === "light" ? "#666666" : "#ffffff";

    return (
        <StyledWrapper {...styles.listWrapper} id={`${carouselSpecificId}`}>
            {title && (
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h1" {...styles.title} color={getTitleColor()}>
                        {title}
                    </Typography>
                    <Link
                        {...styles.readMoreLink}
                        color={getTitleColor()}
                        href={parentPageLink?.url}
                    >
                        {translate('View All')}
                        <Icon src={IconArrowRightCopper} size={9} color={getTitleColor()} />
                    </Link>
                </StyledWrapper>
            )}
            <StyledWrapper className={`carousel-news-container ${id}`} css={newsListCarousel}>
                <Swiper
                    onInit={swiper => {
                        assignSwiper(swiper);
                    }}
                    spaceBetween={22}
                    navigation={{
                        nextEl: `.swiper-button-next.swiper-button-next-${id}`,
                        prevEl: `.swiper-button-prev.swiper-button-prev-${id}`,
                    }}
                    initialSlide={0}
                    onResize={swiper => assignSwiper(swiper)}
                    slidesPerView={slides}
                    loop={false}
                    className="swiper-carousel"
                    wrapperClass="swiper-container"
                    modules={[Navigation, Pagination, Scrollbar, A11y]}
                    pagination={{
                        clickable: true,
                        el: `.swiper-pagination.news-list-carousel-pagination${id}`
                    }}
                    noSwipingClass="no-swiping-elements"
                >

                    {sortedAndPagedPages.map((page: PageProps) => (
                        <SwiperSlide key={`carouselNewsList_${page.id}`}>
                            <NewsCard
                                title={page.fields['title']}
                                image={page.fields["openGraphImage"]}
                                author={page.fields["author"]}
                                publishDate={page.fields["publishDate"]}
                                content={page.fields["newsSummary"] || page.fields["newsContent"]}
                                link={links[page.nodeId]?.url}
                                themeColor={colorTheme}
                                isCarouselItem
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <StyledWrapper className="swiper-navigation">
                    <StyledWrapper
                        className={`swiper-button-prev swiper-button-prev-${id}`}
                        aria-label="carousel-move-left-button"
                    >
                        <Icon
                            src="ChevronLeft"
                            size={24}
                            css={css`
                                & svg {
                                    stroke-width: 3;
                                    color: ${getIconColor()};
                                }
                            `}
                         />
                    </StyledWrapper>
                    <StyledWrapper
                        className={`swiper-pagination news-list-carousel-pagination${id} ${colorTheme === "light" ? "light" : "dark"}`}
                    />
                    <StyledWrapper
                        className={`swiper-button-next swiper-button-next-${id}`}
                        aria-label="carousel-move-right-button"
                    >
                        <Icon
                            src="ChevronRight"
                            size={24}
                            css={css`
                                & svg {
                                    stroke-width: 3;
                                    color: ${getIconColor()};
                                }
                            `}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "News List Carousel",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "parentNewsPage",
                displayName: "Select Parent News Page",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General"
            },
            {
                name: "carouselSpecificId",
                displayName: "Carousel Specific Id/Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "newsType",
                displayName: "News Type",
                editorTemplate: "SystemListDropDownField",
                defaultValue: "",
                fieldType: "General",
                systemListName: "NewsPageType"
            },
            {
                name: "colorTheme",
                displayName: "Theme Color",
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            }
        ],
    },
};

export default widgetModule;
